import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query as firestoreQuery,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { TURKEY_PROVINCES } from "../data/turkeyProvinces";
import "./PopuliUserPage.css";

const POPULI_USERS_COLLECTION = "populi-datas";
const BOT_NAME = "YiviBot";

const PREVIEW_HEADERS = ["Phone_Number", "Sicil_No", "İsim", "Role", "İl", "Aktif"];

const STATUS_LABELS = {
  0: "Onay Bekliyor",
  1: "Kullanım Onayı Bekliyor",
  2: "Aktif",
  3: "Bloklu",
};

const STATUS_TABS = ["2", "1", "0", "3"];

function Icon({ name }) {
  const paths = {
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    excel: <path d="M14 3h5v18H5V3h5m-2 5 5 8m0-8-5 8" />,
    upload: <path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v5h14v-5" />,
    plus: <path d="M12 5v14M5 12h14" />,
    manage: <path d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    users: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />,
    filter: <path d="M4 5h16M7 12h10M10 19h4" />,
  };
  return <svg className="users-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function Modal({ title, wide = false, children, onClose }) {
  return (
    <div className="users-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={`users-modal${wide ? " users-modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="users-modal__header">
          <h2>{title}</h2>
          <button type="button" className="users-icon-button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function statusLabel(step) {
  return STATUS_LABELS[String(step)] || "Bilinmeyen Statü";
}

function normalizeSearchValue(value) {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function matchesTextFilter(value, filter, compact = false) {
  const normalizedFilter = normalizeSearchValue(filter);
  if (!normalizedFilter) return true;

  const normalizedValue = normalizeSearchValue(value);
  if (normalizedValue.includes(normalizedFilter)) return true;

  // Telefon ve sicil numaralarında boşluk, +, tire gibi biçim farklarını yok say.
  return compact
    ? normalizedValue.replaceAll(" ", "").includes(normalizedFilter.replaceAll(" ", ""))
    : false;
}

function excelProvinceValue(row) {
  if (Object.prototype.hasOwnProperty.call(row, "İl")) return row["İl"];
  // Noktasız I ile hazırlanmış dosyaları da geriye dönük olarak kabul et.
  if (Object.prototype.hasOwnProperty.call(row, "Il")) return row.Il;
  return undefined;
}

function canonicalProvince(value) {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) return "";
  const paddedCode = /^\d{1,2}$/.test(rawValue) ? rawValue.padStart(2, "0") : "";
  return TURKEY_PROVINCES.find((province) => (
    province.code === paddedCode
    || normalizeSearchValue(province.name) === normalizeSearchValue(rawValue)
  ))?.name || "";
}

function phoneIdentity(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

function excelUserStep(active, existingUsers) {
  if (Number(active) === 0) return 3;
  if (existingUsers.some((user) => String(user.step) === "2")) return 2;
  return 1;
}

function isVisibleForDepartment(user, department) {
  if (department === "HR") return user.role != null && user.role !== "MİM";
  if (department === "CMI") return user.role == null || user.role === "MİM";
  if (department === "IT") return false;
  return true;
}

async function getCurrentDepartment() {
  if (!auth.currentUser?.uid) return "";
  const snapshot = await getDoc(doc(db, "users", auth.currentUser.uid));
  return snapshot.exists() ? snapshot.data().department || "" : "";
}

async function commitBatches(operations) {
  for (let index = 0; index < operations.length; index += 500) {
    const batch = writeBatch(db);
    operations.slice(index, index + 500).forEach((operation) => operation(batch));
    await batch.commit();
  }
}

export default function PopuliUserPage({ userEmail: emailProp, onUnauthorized }) {
  const [users, setUsers] = useState([]);
  const [department, setDepartment] = useState("");
  const [telegramIdFilter, setTelegramIdFilter] = useState("");
  const [referenceFilter, setReferenceFilter] = useState("");
  const [userNameFilter, setUserNameFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [activeStatus, setActiveStatus] = useState("2");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [managedUser, setManagedUser] = useState(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null);
  const fileInputRef = useRef(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadUsers = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const [snapshot, currentDepartment] = await Promise.all([
        getDocs(firestoreQuery(
          collection(db, POPULI_USERS_COLLECTION),
          where("botname", "==", BOT_NAME),
        )),
        getCurrentDepartment(),
      ]);
      const userList = snapshot.docs
        .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
        .filter((user) => isVisibleForDepartment(user, currentDepartment));
      setUsers(userList);
      setDepartment(currentDepartment);
    } catch (requestError) {
      setError(requestError.message || "Kullanıcılar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadUsers();
  }, [loadUsers, onUnauthorized, userEmail]);

  const matchingUsers = users.filter((user) => (
    matchesTextFilter(user.telegram_id, telegramIdFilter, true)
    && matchesTextFilter(user.refvalue, referenceFilter, true)
    && matchesTextFilter(user.userName, userNameFilter)
    && matchesTextFilter(user.role ?? "Onay bekliyor", roleFilter)
    && matchesTextFilter(user.province, provinceFilter)
  ));
  const filteredUsers = matchingUsers.filter((user) => String(user.step) === activeStatus);
  const statusCounts = matchingUsers.reduce((counts, user) => {
    const status = String(user.step);
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  const hasActiveFilters = Boolean(
    telegramIdFilter || referenceFilter || userNameFilter || roleFilter || provinceFilter,
  );
  const activeUserCount = users.filter((user) => String(user.step) === "2").length;
  const pendingUserCount = users.filter((user) => ["0", "1"].includes(String(user.step))).length;

  function clearFilters() {
    setTelegramIdFilter("");
    setReferenceFilter("");
    setUserNameFilter("");
    setRoleFilter("");
    setProvinceFilter("");
  }

  async function updateUser({ processType, finalReference, name = "", role = "", province = "" }) {
    if (!managedUser) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const previousStep = [1, 2].includes(Number(managedUser.previous_step))
        ? Number(managedUser.previous_step)
        : 1;
      const changes = {
        step: processType === "block"
          ? 3
          : (processType === "unblock"
            ? previousStep
            : (processType === "approve" ? 1 : managedUser.step)),
        request_time: serverTimestamp(),
      };
      if (processType === "block") {
        changes.previous_step = [1, 2].includes(Number(managedUser.step))
          ? Number(managedUser.step)
          : 1;
      }
      if (["approve", "edit"].includes(processType)) {
        changes.refvalue = finalReference;
        changes.userName = name;
        changes.role = role;
        changes.province = province;
      }
      await updateDoc(doc(db, POPULI_USERS_COLLECTION, managedUser.id), changes);
      setManagedUser(null);
      setNotice("Kullanıcı bilgisi başarıyla güncellendi.");
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || "Kullanıcı güncellenemedi.");
      setLoading(false);
    }
  }

  function approveUser(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    updateUser({
      processType: "approve",
      finalReference: String(form.get("reference") || "").trim(),
      name: String(form.get("name") || "").trim(),
      role: String(form.get("role") || ""),
      province: String(form.get("province") || ""),
    });
  }

  function editUser(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    updateUser({
      processType: "edit",
      finalReference: String(form.get("reference") || "").trim(),
      name: String(form.get("name") || "").trim(),
      role: String(form.get("role") || "").trim(),
      province: String(form.get("province") || ""),
    });
  }

  async function addUser(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const telegramId = String(form.get("phone") || "").trim();
    const normalizedPhone = phoneIdentity(telegramId);
    if (!normalizedPhone) {
      setError("Telefon numarası boş bırakılamaz.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    try {
      const snapshot = await getDocs(firestoreQuery(
        collection(db, POPULI_USERS_COLLECTION),
        where("botname", "==", BOT_NAME),
      ));
      const duplicate = snapshot.docs.some((userDoc) => (
        phoneIdentity(userDoc.data().telegram_id) === normalizedPhone
      ));
      if (duplicate) throw new Error("Bu telefon numarasıyla kayıtlı bir kullanıcı zaten var.");

      const newUserRef = doc(collection(db, POPULI_USERS_COLLECTION));
      await setDoc(newUserRef, {
        telegram_id: telegramId,
        botname: BOT_NAME,
        refvalue: String(form.get("reference") || "").trim(),
        request_time: serverTimestamp(),
        step: 1,
        role: String(form.get("role") || "").trim(),
        userName: String(form.get("name") || "").trim(),
        province: String(form.get("province") || ""),
      });
      setAddUserOpen(false);
      setNotice("Kullanıcı başarıyla eklendi.");
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || "Kullanıcı eklenemedi.");
      setLoading(false);
    }
  }

  function exportUsers() {
    const rows = users.map((user) => ({
      "Telefon Numarası": user.telegram_id,
      "Sicil No": user.refvalue,
      "Kullanıcı Adı": user.userName,
      Rol: user.role ?? "Onay bekliyor",
      İl: user.province || "",
      Statü: statusLabel(user.step),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Users");
    XLSX.writeFile(workbook, "Users.xlsx");
  }

  async function selectExcelFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      setExcelFile(file);
      setExcelPreview(rows);
    } catch (parseError) {
      setExcelFile(null);
      setExcelPreview(null);
      setError("Excel dosyası okunamadı.");
    }
  }

  function closeExcelPreview() {
    setExcelFile(null);
    setExcelPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function uploadExcel() {
    if (!excelFile || !excelPreview) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const allUsersSnapshot = await getDocs(firestoreQuery(
        collection(db, POPULI_USERS_COLLECTION),
        where("botname", "==", BOT_NAME),
      ));
      const usersByTelegramId = new Map();
      allUsersSnapshot.docs.forEach((userDoc) => {
        const user = { id: userDoc.id, ...userDoc.data() };
        const identity = phoneIdentity(user.telegram_id);
        if (!usersByTelegramId.has(identity)) usersByTelegramId.set(identity, []);
        usersByTelegramId.get(identity).push(user);
      });

      // Aynı telefon Excel'de birden fazla kez bulunuyorsa dosyadaki son satır geçerlidir.
      const lastExcelRowsByTelegramId = new Map();
      excelPreview.forEach((row) => {
        const telegramId = String(row.Phone_Number ?? "").trim();
        if (!telegramId) throw new Error("Phone_Number alanı boş bırakılamaz.");
        lastExcelRowsByTelegramId.set(phoneIdentity(telegramId), { row, telegramId });
      });

      const operations = [];
      lastExcelRowsByTelegramId.forEach(({ row, telegramId }, identity) => {
        const existingUsers = usersByTelegramId.get(identity) || [];
        const step = excelUserStep(row.Aktif, existingUsers);

        const values = {
          telegram_id: telegramId,
          botname: BOT_NAME,
          refvalue: String(row.Sicil_No ?? ""),
          request_time: serverTimestamp(),
          step,
          role: String(row.Role ?? ""),
          userName: String(row["İsim"] ?? ""),
        };
        if (step === 3) {
          values.previous_step = existingUsers.some((user) => String(user.step) === "2") ? 2 : 1;
        }

        const rawProvince = excelProvinceValue(row);
        if (rawProvince !== undefined) {
          const province = canonicalProvince(rawProvince);
          if (!province) {
            throw new Error(`Geçersiz veya boş İl değeri: ${String(rawProvince || "(boş)")}`);
          }
          values.province = province;
        } else {
          const existingProvince = existingUsers.find((user) => user.province)?.province;
          if (existingProvince) values.province = existingProvince;
        }

        existingUsers.forEach((existingUser) => {
          operations.push((batch) => batch.delete(
            doc(db, POPULI_USERS_COLLECTION, existingUser.id),
          ));
        });
        const newUserRef = doc(collection(db, POPULI_USERS_COLLECTION));
        operations.push((batch) => batch.set(newUserRef, values));
      });

      await commitBatches(operations);
      closeExcelPreview();
      setNotice("Excel kullanıcı verileri başarıyla yüklendi.");
      await loadUsers();
    } catch (requestError) {
      setError(requestError.message || "Excel verileri yüklenemedi.");
      setLoading(false);
    }
  }

  const existingTelegramIds = useMemo(
    () => new Set(users.map((user) => String(user.telegram_id))),
    [users],
  );

  if (!userEmail) {
    return <div className="users-page users-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="users-page">
      <section className="users-shell">
        <header className="users-page-header">
          <div>
            <span className="users-eyebrow">{department ? `${department} Ekibi` : "Kullanıcı Yönetimi"}</span>
            <h1>Kullanıcılar</h1>
            <p>Kullanıcı bilgilerini güncelleyin, erişim durumlarını takip edin ve yeni kayıtları Excel’den ekleyin.</p>
          </div>
          <div className="users-summary" aria-label="Kullanıcı özeti">
            <div className="users-summary__item users-summary__item--total"><span>Toplam kullanıcı</span><strong>{users.length}</strong></div>
            <div className="users-summary__item users-summary__item--active"><span>Aktif kullanıcı</span><strong>{activeUserCount}</strong></div>
            <div className="users-summary__item users-summary__item--pending"><span>İşlem bekleyen</span><strong>{pendingUserCount}</strong></div>
          </div>
        </header>

        <div className="users-toolbar">
          <div className="users-toolbar__title">
            <span className="users-toolbar__icon"><Icon name="users" /></span>
            <div><strong>Kullanıcı listesi</strong><span>{filteredUsers.length} kayıt görüntüleniyor</span></div>
          </div>
          <div className="users-toolbar__actions">
            <button className="users-button users-button--secondary" onClick={loadUsers} disabled={loading}>
              <Icon name="refresh" /> Yenile
            </button>
            {department === "HR" && (
              <>
                <button className="users-button users-button--success" type="button" onClick={() => setAddUserOpen(true)}>
                  <Icon name="plus" /> Kullanıcı Ekle
                </button>
                <label className="users-button users-button--upload">
                  <Icon name="upload" /> Excel’den Ekle
                  <input
                    ref={fileInputRef}
                    aria-label="Excel’den Kullanıcı Ekle"
                    className="users-visually-hidden"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={selectExcelFile}
                  />
                </label>
              </>
            )}
            <button className="users-button users-button--excel" onClick={exportUsers} disabled={!users.length}>
              <Icon name="excel" /> Excel’e Aktar
            </button>
          </div>
        </div>

        {error && <div className="users-alert users-alert--error" role="alert">{error}</div>}
        {notice && <div className="users-alert users-alert--success" role="status">{notice}</div>}

        <div className="users-filters">
          <div className="users-filters__heading">
            <div><Icon name="filter" /><span>Kullanıcı ara ve filtrele</span><small>Sonuçlar yazarken otomatik güncellenir</small></div>
            {hasActiveFilters && <button type="button" onClick={clearFilters}>Filtreleri temizle</button>}
          </div>
          <div className="users-filters__grid">
            <label><span>Telefon Numarası</span><input aria-label="Telefon numarası filtrele" value={telegramIdFilter} onChange={(event) => setTelegramIdFilter(event.target.value)} placeholder="Telefon numarası" autoComplete="off" /></label>
            <label><span>Referans Kodu</span><input aria-label="Referans kodu filtrele" value={referenceFilter} onChange={(event) => setReferenceFilter(event.target.value)} placeholder="Referans kodu" autoComplete="off" /></label>
            <label><span>Kullanıcı Adı</span><input aria-label="Kullanıcı adı filtrele" value={userNameFilter} onChange={(event) => setUserNameFilter(event.target.value)} placeholder="Kullanıcı adı" autoComplete="off" /></label>
            <label><span>Rol</span><input aria-label="Rol filtrele" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} placeholder="Rol" autoComplete="off" /></label>
            <label><span>İl</span><input aria-label="İl filtrele" value={provinceFilter} onChange={(event) => setProvinceFilter(event.target.value)} placeholder="İl" autoComplete="off" /></label>
          </div>
        </div>

        <div className="users-status-tabs" role="tablist" aria-label="Kullanıcı statüsü">
          {STATUS_TABS.map((status, index) => (
            <button
              key={status}
              type="button"
              role="tab"
              id={`users-status-tab-${status}`}
              aria-controls="users-status-panel"
              aria-selected={activeStatus === status}
              tabIndex={activeStatus === status ? 0 : -1}
              className={`users-status-tab users-status-tab--${status}`}
              onClick={() => setActiveStatus(status)}
              onKeyDown={(event) => {
                const nextIndex = {
                  ArrowRight: (index + 1) % STATUS_TABS.length,
                  ArrowLeft: (index + STATUS_TABS.length - 1) % STATUS_TABS.length,
                  Home: 0,
                  End: STATUS_TABS.length - 1,
                }[event.key];
                if (nextIndex === undefined) return;
                event.preventDefault();
                setActiveStatus(STATUS_TABS[nextIndex]);
                event.currentTarget.parentElement.children[nextIndex].focus();
              }}
            >
              <span className="users-status-tab__dot" aria-hidden="true" />
              <span>{STATUS_LABELS[status]}</span>
              <span className="users-status-tab__count">{statusCounts[status] || 0}</span>
            </button>
          ))}
        </div>

        <div
          className="users-table-wrap users-table-wrap--main"
          role="tabpanel"
          id="users-status-panel"
          aria-labelledby={`users-status-tab-${activeStatus}`}
          aria-busy={loading}
          tabIndex={0}
        >
          <table className="users-table">
            <thead>
              <tr><th>Telefon Numarası</th><th>Referans Kodu</th><th>Kullanıcı Adı</th><th>Rol</th><th>İl</th><th>Statü</th><th>İşlemler</th></tr>
            </thead>
            <tbody>
              {!filteredUsers.length && <tr><td className="users-table__empty" colSpan="7"><strong>Kullanıcı bulunamadı</strong><span>Seçili durum veya filtrelerle eşleşen bir kayıt yok.</span></td></tr>}
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td data-label="Telefon Numarası">{user.telegram_id || "—"}</td>
                  <td data-label="Referans Kodu">{user.refvalue || "—"}</td>
                  <td data-label="Kullanıcı Adı">
                    <div className="users-person">
                      <span>{String(user.userName || "?").trim().charAt(0).toLocaleUpperCase("tr-TR")}</span>
                      <strong>{user.userName || "—"}</strong>
                    </div>
                  </td>
                  <td data-label="Rol" className={user.role == null ? "users-role--pending" : ""}>{user.role ?? "Onay bekliyor"}</td>
                  <td data-label="İl">{user.province || "—"}</td>
                  <td data-label="Statü"><span className={`users-status users-status--${user.step}`}>{statusLabel(user.step)}</span></td>
                  <td data-label="İşlemler">
                    <div className="users-row-actions">
                      <button className="users-edit-button" onClick={() => setManagedUser(user)} aria-label="Kullanıcıyı yönet">
                        <Icon name="manage" /> {String(user.step) === "0" ? "İncele" : "Düzenle"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {addUserOpen && (
        <Modal title="Yeni Kullanıcı Ekle" onClose={() => setAddUserOpen(false)}>
          <form className="users-form" onSubmit={addUser}>
            <p className="users-form__hint">Yeni kullanıcı “Kullanım Onayı Bekliyor” durumunda oluşturulur. Tüm alanları eksiksiz doldurun.</p>
            <label>Telefon Numarası<input name="phone" type="tel" placeholder="Örn. +90 555 111 22 33" required autoFocus /></label>
            <label>İsim<input name="name" placeholder="Ad soyad" required /></label>
            <label>Sicil No<input name="reference" placeholder="Sicil numarası" required /></label>
            <label>Rol<input name="role" placeholder="Kullanıcı rolü" required /></label>
            <label>İl<select name="province" defaultValue="" required><option value="">İl seçin</option>{TURKEY_PROVINCES.map((province) => <option key={province.code} value={province.name}>{province.name}</option>)}</select></label>
            <div className="users-modal__actions">
              <button type="button" className="users-button users-button--ghost" onClick={() => setAddUserOpen(false)}>İptal</button>
              <button type="submit" className="users-button users-button--success">Kullanıcıyı Ekle</button>
            </div>
          </form>
        </Modal>
      )}

      {managedUser && (
        <Modal title={String(managedUser.step) === "0" ? "Kullanıcı Onayı" : "Kullanıcı Bilgilerini Düzenle"} onClose={() => setManagedUser(null)}>
          {String(managedUser.step) === "0" ? (
            <form className="users-form" onSubmit={approveUser}>
              <p className="users-form__hint">Kullanıcı erişimini onaylamadan önce bilgileri kontrol edip eksik alanları tamamlayın.</p>
              <label>İsim<input name="name" defaultValue={managedUser.userName || ""} required autoFocus /></label>
              <label>Sicil No<input name="reference" defaultValue={managedUser.refvalue || ""} required /></label>
              <label>Rol<select name="role" defaultValue="MİM"><option value="MİM">MİM</option></select></label>
              <label>İl<select name="province" defaultValue={canonicalProvince(managedUser.province)} required><option value="">İl seçin</option>{TURKEY_PROVINCES.map((province) => <option key={province.code} value={province.name}>{province.name}</option>)}</select></label>
              <div className="users-modal__actions">
                <button type="button" className="users-button users-button--danger" onClick={() => updateUser({ processType: "block", finalReference: managedUser.refvalue || "" })}>Engelle</button>
                <button type="submit" className="users-button users-button--success">Onayla</button>
              </div>
            </form>
          ) : (
            <form className="users-form" onSubmit={editUser}>
              <p className="users-form__hint">Bilgileri güncelleyip kaydedebilirsiniz. Telefon numarası hesap eşleştirmesi için değiştirilemez.</p>
              <label>Telefon Numarası<input value={managedUser.telegram_id || ""} disabled /><small>Bu alan değiştirilemez.</small></label>
              <label>İsim<input name="name" defaultValue={managedUser.userName || ""} required autoFocus /></label>
              <label>Sicil No<input name="reference" defaultValue={managedUser.refvalue || ""} required /></label>
              <label>Rol<input name="role" defaultValue={managedUser.role || ""} required /></label>
              <label>İl<select name="province" defaultValue={canonicalProvince(managedUser.province)} required><option value="">İl seçin</option>{TURKEY_PROVINCES.map((province) => <option key={province.code} value={province.name}>{province.name}</option>)}</select></label>
              <div className="users-modal__actions">
                <button type="button" className="users-button users-button--ghost" onClick={() => setManagedUser(null)}>İptal</button>
                {String(managedUser.step) === "3" ? (
                  <button type="button" className="users-button users-button--success" onClick={() => updateUser({ processType: "unblock" })}>Engeli Kaldır</button>
                ) : (
                  <button type="button" className="users-button users-button--danger" onClick={() => updateUser({ processType: "block" })}>Engelle</button>
                )}
                <button type="submit" className="users-button users-button--success">Kaydet</button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {excelPreview && (
        <Modal title="Excel Önizleme" wide onClose={closeExcelPreview}>
          <p className="users-form__hint">Dosyayı yüklemeden önce satırları kontrol edin. Sistemde bulunan telefon numaraları kırmızı renkle işaretlenir.</p>
          {excelPreview.length ? (
            <div className="users-table-wrap users-table-wrap--modal">
              <table className="users-table users-table--preview">
                <thead><tr>{PREVIEW_HEADERS.map((header) => <th key={header}>{header}</th>)}</tr></thead>
                <tbody>
                  {excelPreview.map((row, index) => {
                    const duplicate = existingTelegramIds.has(String(row.Phone_Number));
                    return (
                      <tr className={duplicate ? "users-preview--duplicate" : ""} key={`${row.Phone_Number || "row"}-${index}`}>
                        {PREVIEW_HEADERS.map((header) => <td key={header}>{String(row[header] ?? "")}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p>Excel dosyasında veri bulunamadı.</p>}
          <p className="users-preview-note">{excelPreview.length} kayıt yüklenmeye hazır.</p>
          <div className="users-modal__actions">
            <button type="button" className="users-button users-button--ghost" onClick={closeExcelPreview}>İptal</button>
            <button type="button" className="users-button users-button--secondary" onClick={uploadExcel} disabled={!excelPreview.length}>Yükle</button>
          </div>
        </Modal>
      )}

      {loading && <div className="users-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
